import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Settings as SettingsIcon, ShieldCheck, ShieldOff, RefreshCw, History, Plug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

import ReauthModal from "@/components/settings/ReauthModal";
import IntegrationCard from "@/components/settings/IntegrationCard";
import EditKeysDialog from "@/components/settings/EditKeysDialog";
import AuditLogPanel from "@/components/settings/AuditLogPanel";

import { listIntegrations, getAdminUser } from "@/lib/api";

const REAUTH_KEY = "tj_settings_reauth";

/**
 * Settings page — currently a single tab "Integrations & Secrets" plus
 * a side "Audit Log" tab. Owner (Beckett) only; other admins see a denial.
 */
export default function SettingsPanel({ onSessionExpired }) {
  const user = getAdminUser();
  const isOwner = user?.id === "beckett";

  const [reauthToken, setReauthToken] = useState(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  // Restore reauth token from sessionStorage (in-memory across navigations)
  useEffect(() => {
    if (!isOwner) return;
    try {
      const raw = sessionStorage.getItem(REAUTH_KEY);
      if (raw) {
        const { token, exp } = JSON.parse(raw);
        if (token && exp && Date.now() / 1000 < exp) {
          setReauthToken(token);
          return;
        }
        sessionStorage.removeItem(REAUTH_KEY);
      }
    } catch { /* ignore */ }
    setReauthOpen(true);
  }, [isOwner]);

  const loadServices = useCallback(async () => {
    if (!reauthToken) return;
    setLoading(true);
    try {
      const res = await listIntegrations(reauthToken);
      setServices(res.services || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Reauth token expired
        sessionStorage.removeItem(REAUTH_KEY);
        setReauthToken(null);
        setReauthOpen(true);
        toast.info("Re-authentication expired. Please confirm your password again.");
      } else if (status === 403) {
        toast.error("Owner-only area.");
      } else {
        toast.error(err?.response?.data?.detail || "Could not load integrations");
      }
    } finally {
      setLoading(false);
    }
  }, [reauthToken]);

  useEffect(() => { loadServices(); }, [loadServices]);

  if (!isOwner) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="settings-not-owner">
        <span className="inline-grid place-items-center h-14 w-14 rounded-full mb-4"
              style={{ background: "rgba(155,28,28,0.08)", color: "#9B1C1C" }}>
          <ShieldOff className="h-6 w-6" />
        </span>
        <h2 className="font-display text-xl font-semibold" style={{ color: "#0B2545" }}>
          Settings is restricted to the Owner account
        </h2>
        <p className="mt-2 text-sm" style={{ color: "#64748B" }}>
          You&apos;re signed in as <span className="font-semibold">{user?.name}</span> ({user?.role}). Only Beckett can manage integrations and secrets.
        </p>
      </div>
    );
  }

  const handleReauthSuccess = (token, exp) => {
    setReauthToken(token);
    setReauthOpen(false);
    try { sessionStorage.setItem(REAUTH_KEY, JSON.stringify({ token, exp })); } catch { /* ignore */ }
  };

  const lockNow = () => {
    setReauthToken(null);
    sessionStorage.removeItem(REAUTH_KEY);
    setServices([]);
    setReauthOpen(true);
    toast.info("Settings re-locked.");
  };

  if (!reauthToken) {
    return (
      <>
        <div className="max-w-md mx-auto py-16 text-center" data-testid="settings-locked">
          <span className="inline-grid place-items-center h-14 w-14 rounded-full mb-4"
                style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
            <SettingsIcon className="h-6 w-6" />
          </span>
          <h2 className="font-display text-xl font-semibold" style={{ color: "#0B2545" }}>
            Settings is locked
          </h2>
          <p className="mt-2 text-sm" style={{ color: "#64748B" }}>
            Re-enter your password to manage integrations and secrets.
          </p>
          <Button
            className="mt-4"
            onClick={() => setReauthOpen(true)}
            style={{ background: "#0B2545", color: "#fff" }}
            data-testid="settings-unlock-button"
          >
            Unlock Settings
          </Button>
        </div>
        <ReauthModal
          open={reauthOpen}
          onSuccess={handleReauthSuccess}
          onCancel={() => setReauthOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-panel">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: "#0B2545" }}>
            Settings
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "#64748B" }}>
            Manage third-party integrations without editing environment variables. All actions are audited.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium"
                style={{ background: "rgba(22,101,52,0.10)", color: "#166534" }}
                data-testid="settings-unlocked-badge">
            <ShieldCheck className="h-3 w-3" /> Owner unlocked · 15 min
          </span>
          <Button variant="outline" size="sm" onClick={loadServices} disabled={loading} data-testid="settings-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={lockNow} data-testid="settings-lock">
            <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
            Lock
          </Button>
        </div>
      </div>

      <Tabs defaultValue="integrations">
        <TabsList>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Plug className="h-3.5 w-3.5 mr-1.5" />
            Integrations & Secrets
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <History className="h-3.5 w-3.5 mr-1.5" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="mt-5">
          {loading && services.length === 0 ? (
            <div className="grid place-items-center py-16">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="integrations-grid">
              {services.map((svc) => (
                <IntegrationCard
                  key={svc.key}
                  service={svc}
                  reauthToken={reauthToken}
                  onEdit={(s) => setEditing(s)}
                  onChanged={loadServices}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-5">
          <AuditLogPanel reauthToken={reauthToken} />
        </TabsContent>
      </Tabs>

      <EditKeysDialog
        open={!!editing}
        service={editing}
        reauthToken={reauthToken}
        onClose={() => setEditing(null)}
        onSaved={loadServices}
      />
    </div>
  );
}
